import safeDecode from "@core/utils/safeDecode";

/**
 * How long the article goes on putting the anchor back after it last changed.
 *
 * Long enough for the paint that follows a change to be over, short enough that a reader who lands on the
 * article and does nothing is not scrolled under their hands.
 */
const SETTLE_MS = 300;

/** What the reader does when the position is theirs to decide, not the link's. */
const TAKEOVER_EVENTS = ["wheel", "touchstart", "keydown"] as const;

/**
 * Keeps the article at the anchor the address names while the page is still settling under it.
 *
 * A link into an article points at content that is not always there when the article is: a block loaded
 * lazily — an OpenAPI reference above all — paints long after its page, and in the editor the caret is put
 * at the start of the document a moment after mount, which takes the article back to the top with the
 * anchor left somewhere below. One attempt at a fixed delay answered neither: too early it found nothing,
 * too late it fought the reader.
 *
 * So the article is followed instead of timed. Every change under it is a reason to put the anchor back,
 * coalesced to one scroll per frame; the hold ends when the changes stop, when the address stops naming
 * this anchor, or the moment the reader scrolls for themselves.
 */
const holdArticleAnchor = (article: HTMLElement, anchorId: string): (() => void) => {
	let quiet: ReturnType<typeof setTimeout>;
	let frame = 0;
	const cleanups: (() => void)[] = [];

	const release = () => {
		clearTimeout(quiet);
		cancelAnimationFrame(frame);
		while (cleanups.length) cleanups.pop()?.();
	};

	const apply = () => {
		frame = 0;
		// The address is read again every time: it is the reader's, and while the page settles they may follow
		// another link or clear the hash — after which this anchor is not the one to hold.
		if (safeDecode(window.location.hash.substring(1)) !== anchorId) return release();

		// By id and not by selector: an id is unique in the document, so there is nothing to escape, and what
		// the anchor names has to be in this article — `getElementById` would find one in a floating panel too.
		const anchor = document.getElementById(anchorId);
		if (article.contains(anchor)) anchor?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const onChanged = () => {
		clearTimeout(quiet);
		quiet = setTimeout(release, SETTLE_MS);
		// Changes come in bursts — a whole block at once — and one scroll a frame answers all of them.
		if (!frame) frame = requestAnimationFrame(apply);
	};

	const observer = new MutationObserver(onChanged);
	observer.observe(article, { childList: true, subtree: true });
	cleanups.push(() => observer.disconnect());

	for (const event of TAKEOVER_EVENTS) {
		window.addEventListener(event, release, { passive: true });
		cleanups.push(() => window.removeEventListener(event, release));
	}

	onChanged();
	return release;
};

export default holdArticleAnchor;
